import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

// modules
import { TextMaskModule } from 'angular2-text-mask';
import { NgSelectModule } from '@ng-select/ng-select';

// store
import { StoreModule } from '@ngrx/store';
import { appReducer } from './app-reducer';

// angular material
import { MaterialModule } from './material.module';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

// routing
import { AppRoutingModule } from './app-routing.module';

// bootstrap
import { PopoverModule } from 'ngx-bootstrap/popover';

// components
import { AppComponent } from './app.component';
import { AddressFormComponent } from './components/custom-forms/address-form.component';
import { MedicalConditionsComponent } from './components/custom-forms/medical-conditions.component';
import { PercentageAllocationComponent } from './components/custom-forms/percentage-allocation.component';
import { PercentageTableComponent } from './components/custom-forms/percentage-table.component';
import { VULPercentageTableComponent } from './components/custom-forms/vul-percentage-table.component';
import { BloodPressureFormComponent } from './components/custom-forms/blood-pressure-form.component';
import { DynamicFormComponent } from './components/dynamic-form.component';
import { DynamicFormQuestionComponent } from './components/dynamic-form-question.component';
import { ErrorBlankApplicationComponent } from './components/error-blank-application.component';
import { ErrorPageComponent } from './components/error-page.component';
import { FindApplicationComponent } from './components/find-application.component';
import { FindApplicationDisclosureComponent } from './components/find-application.disclosure.component';
import { LengthFormComponent } from './components/custom-forms/length-form.component';
import { NameFormComponent } from './components/custom-forms/name-form.component';
import { PhysicianFormComponent } from './components/custom-forms/physician-form.component';
import { MultipleResponseComponent } from './components/custom-forms/multiple-response.component';
import { ProducerWelcomeComponent } from './components/producer-welcome.component';
import { DemoWelcomeComponent } from './components/demo-welcome.component';
import { MiniPortalComponent } from './components/producer-mini-portal.component';
import { DemoResumeApplicationComponent } from './components/demo-resume-app.component';
import { ProducerDemoResumeApplicationComponent } from './components/producer-demo-resume-app.component';
import { DebugPageComponent } from './components/debug-page.component';
import { ProcessingComponent } from './components/processing.component';
import { ProcessingExternalComponent } from './components/processing-external.component';
import { DecisionDetailsComponent } from './components/decision-details.component';
import { DemoDecisionComponent } from './components/demo-decision.component';
import { DeclarationsComponent } from './components/declarations.component';
import { DisclosuresComponent } from './components/disclosures.component';
import { DemoSubnavComponent } from './components/demo-subnav.component';
import { SubheaderComponent } from './components/subheader.component';
import { ThankYouComponent } from './components/thank-you.component';
import { ConfirmationTimeoutComponent } from './components/confirmation-timeout.component';
import { PrintPreviewComponent } from './components/print-preview.component';
import { SavePreviewComponent } from './components/save-preview.component';
import { BeneficiaryAllocationComponent } from './components/custom-forms/beneficiary-allocation.component';
import { LogoutSvgComponent } from './components/svg-components/logout-svg.component';
import { LockSvgComponent } from './components/svg-components/lock-svg.component';
import { PendingBlocksAlertComponent } from './components/pending-blocks-alert.component';
import { FileUploadComponent } from './components/custom-forms/file-upload.component';
import { OwnerCountComponent } from './components/custom-forms/owner-count.component';

// global components
import { ModalComponent } from 'src/app/components/modal.component';
import { PopoverComponent } from 'src/app/components/popover.component';
import { ErrorMessagesComponent } from 'src/app/components/error-messages.component';
import { AutocompleteComponent } from 'src/app/components/autocomplete.component';
import { ErrorComponent } from 'src/app/components/error.component';
import { ErrorSessionComponent } from './components/error-session.component';
import { AddressAutocompleteComponent } from 'src/app/components/address-autocomplete.component';

// directives
import { CleaveDirective } from 'src/app/directives/cleave.directive';
import { DragnDropDirective } from 'src/app/directives/drag-n-drop.directive';

// services
import { ApiService } from 'src/app/services/api.service';
import { ProcessorService } from './services/processor.service';
import { NavigationService } from './services/navigation.service';
import { QuestionControlService } from './services/question-control.service';
import { QuestionService } from './services/question.service';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { ErrorPageService } from './services/error-page.service';
import { MatSortModule } from '@angular/material/sort';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { DisableDirective } from './components/disable.directive';
import { PrimaryLinkSvgComponent } from './components/svg-components/primary-link-icon.component';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MismatchWarningComponent } from './components/mismatch-warning.component';
import { MatCardModule } from '@angular/material/card';
import { HeaderPortalLinkComponent }  from './components/svg-components/header-back-to-portal.component';
import { PortalLinkComponent } from './components/svg-components/back-to-portal-link.component';
import { CommonService } from './services/common.service';

@NgModule({
  declarations: [
    AddressFormComponent,
    AppComponent,
    MedicalConditionsComponent,
    PercentageAllocationComponent,
    PercentageTableComponent,
    VULPercentageTableComponent,
    BloodPressureFormComponent,
    DemoDecisionComponent,
    DecisionDetailsComponent,
    DeclarationsComponent,
    DisclosuresComponent,
    DynamicFormComponent,
    DynamicFormQuestionComponent,
    ErrorBlankApplicationComponent,
    ErrorMessagesComponent,
    ErrorPageComponent,
    FindApplicationComponent,
    FindApplicationDisclosureComponent,
    DemoResumeApplicationComponent,
    ProducerDemoResumeApplicationComponent,
    LengthFormComponent,
    LogoutSvgComponent,
    LockSvgComponent,
    PrimaryLinkSvgComponent,
    PortalLinkComponent,
    HeaderPortalLinkComponent,
    MultipleResponseComponent,
    ModalComponent,
    MiniPortalComponent,
    NameFormComponent,
    PhysicianFormComponent,
    PopoverComponent,
    ProducerWelcomeComponent,
    DemoWelcomeComponent,
    ProcessingComponent,
    ProcessingExternalComponent,
    DemoSubnavComponent,
    SubheaderComponent,
    DebugPageComponent,
    AutocompleteComponent,
    ErrorComponent,
    ErrorSessionComponent,
    ThankYouComponent,
    ConfirmationTimeoutComponent,
    PrintPreviewComponent,
    SavePreviewComponent,
    BeneficiaryAllocationComponent,
    CleaveDirective,
    DisableDirective,
    DragnDropDirective,
    PendingBlocksAlertComponent,
    FileUploadComponent,
    MismatchWarningComponent,
    AddressAutocompleteComponent,
    OwnerCountComponent,
  ],
  entryComponents: [
    ModalComponent,
    FindApplicationDisclosureComponent,
    ConfirmationTimeoutComponent,
    PendingBlocksAlertComponent,
    MismatchWarningComponent,
  ],
  imports: [
    NgSelectModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    PopoverModule.forRoot(),
    ReactiveFormsModule,
    StoreModule.forRoot({ sections: appReducer }, {
      runtimeChecks: {
        // to avoid readonly object error during dev build
        strictStateImmutability: false,
        strictActionImmutability: false
      }
    }),
    MaterialModule,
    TextMaskModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    NgbPopoverModule,
    MatDialogModule,
    MatSortModule,
    MatListModule,
    MatSelectModule,
    PdfViewerModule,
    MatCardModule
  ],
  providers: [
    ApiService,
    ErrorPageService,
    ModalComponent,
    ProcessorService,
    NavigationService,
    QuestionControlService,
    QuestionService,
    CommonService,
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: [] },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
